import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  FileText, Plus, Send, Trash2, Edit3, X,
  Clock, CheckCircle, XCircle, CreditCard, Search,
  ChevronDown, ChevronUp, Building2, Download, RefreshCw,
  Bell, BarChart3, TrendingUp, Banknote, Link2, Unlink
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import AttachDevisModal from '../components/AttachDevisModal';

const API = process.env.REACT_APP_BACKEND_URL;

// Les clés de statut (en_attente, accepte, ...) viennent du backend et ne sont JAMAIS traduites :
// labelKey pointe vers le libellé i18n affiché à l'utilisateur.
const statusConfig = {
  en_attente: { labelKey: 'devisDepl.status.pending', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  accepte: { labelKey: 'devisDepl.status.accepted', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  refuse: { labelKey: 'devisDepl.status.refused', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
  acompte_verse: { labelKey: 'devisDepl.status.depositPaid', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CreditCard },
  paye: { labelKey: 'devisDepl.status.paid', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
};

// Valeurs de mode de paiement envoyées au backend (NE PAS traduire) ; le map pointe
// vers les clés i18n utilisées uniquement pour l'AFFICHAGE des boutons.
const PAYMENT_METHODS = ['Espèces', 'CB', 'Virement', 'Mixte'];
const PAYMENT_METHOD_LABEL_KEYS = {
  'Espèces': 'devisDepl.payment.cash',
  'CB': 'devisDepl.payment.card',
  'Virement': 'devisDepl.payment.transfer',
  'Mixte': 'devisDepl.payment.mixed',
};

const DevisDeplacementPage = () => {
  const { api, isAdmin } = useAuth();
  const { t, tError, formatCurrency, formatDate } = useLanguage();
  const [devisList, setDevisList] = useState([]);
  const [stats, setStats] = useState({});
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sendingId, setSendingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [pendingReminders, setPendingReminders] = useState(0);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [paymentModal, setPaymentModal] = useState({ show: false, devisId: '', devisRef: '', total: 0 });
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'Espèces', note: '' });
  const [processingPayment, setProcessingPayment] = useState(false);
  const [processingAction, setProcessingAction] = useState('');
  const [devisRequests, setDevisRequests] = useState([]);
  const [showRequests, setShowRequests] = useState(false);
  const [updatingRequestId, setUpdatingRequestId] = useState(null);
  const [refuseRequestId, setRefuseRequestId] = useState(null);
  const [refuseMotif, setRefuseMotif] = useState('');
  const [sumupRequests, setSumupRequests] = useState([]);
  const [showSumupRequests, setShowSumupRequests] = useState(false);
  const [sumupLinkInput, setSumupLinkInput] = useState({});
  const [attachModal, setAttachModal] = useState({ show: false, devisId: '', devisRef: '', devisTotal: 0 });
  const [disableOnlinePayment, setDisableOnlinePayment] = useState(false);

  const [form, setForm] = useState({
    client_nom: '', client_tel: '', client_email: '', client_adresse: '',
    description: '', inclure_deplacement: true, accept_online_payment: false,
    lignes: [{ designation: '', quantite: 1, prix: 0 }]
  });

  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingClients, setSearchingClients] = useState(false);
  const suggestionsRef = React.useRef(null);

  const searchClients = useCallback(async (query) => {
    if (!query || query.length < 2) { setClientSuggestions([]); return; }
    setSearchingClients(true);
    try {
      const res = await api.get(`/admin/devis/search-clients?q=${encodeURIComponent(query)}`);
      setClientSuggestions(res.data || []);
      setShowSuggestions(true);
    } catch (e) { console.error(e); }
    finally { setSearchingClients(false); }
  }, [api]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (form.client_nom && !editingId) searchClients(form.client_nom);
    }, 300);
    return () => clearTimeout(timer);
  }, [form.client_nom, editingId, searchClients]);

  const selectClient = (c) => {
    setForm(f => ({
      ...f,
      client_nom: c.nom || f.client_nom,
      client_tel: c.tel || f.client_tel,
      client_email: c.email || f.client_email,
      client_adresse: c.adresse || f.client_adresse,
    }));
    setShowSuggestions(false);
    setClientSuggestions([]);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [devisRes, statsRes, reminderRes, requestsRes, sumupRes, settingsRes] = await Promise.all([
        api.get('/admin/devis'),
        api.get('/admin/devis/stats'),
        api.get('/admin/devis/pending-reminders?days_threshold=3'),
        api.get('/admin/devis-requests'),
        api.get('/admin/sumup-requests'),
        api.get('/company-settings').catch(() => ({ data: {} })),
      ]);
      setDevisList(devisRes.data || []);
      setStats(statsRes.data || {});
      setPendingReminders(reminderRes.data?.pending_reminders || 0);
      setDevisRequests(requestsRes.data || []);
      setSumupRequests(sumupRes.data || []);
      setDisableOnlinePayment(settingsRes.data?.document_settings?.disable_online_payment || false);
    } catch (err) {
      console.error('Error fetching devis:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const updateRequestStatus = async (requestId, status, staffNote = '') => {
    setUpdatingRequestId(requestId);
    try {
      await api.put(`/admin/devis-requests/${requestId}/status`, { status, staff_note: staffNote });
      setDevisRequests(prev => prev.map(r => r.id === requestId ? { ...r, status, staff_note: staffNote || r.staff_note } : r));
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisDepl.errGeneric'));
    } finally {
      setUpdatingRequestId(null);
    }
  };

  const prefillFromRequest = (req) => {
    setForm(f => ({
      ...f,
      client_nom: req.source_name || '',
      client_tel: req.source_tel || '',
      client_email: req.source_email || '',
      client_adresse: req.adresse || '',
      description: `${req.titre ? req.titre + ' - ' : ''}${req.description}`,
    }));
    setShowForm(true);
    updateRequestStatus(req.id, 'en_cours');
  };

  const handleRefuse = async (requestId) => {
    setUpdatingRequestId(requestId);
    try {
      await api.put(`/admin/devis-requests/${requestId}/status`, { status: 'refuse', motif_refus: refuseMotif });
      setDevisRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'refuse', motif_refus: refuseMotif } : r));
      setRefuseRequestId(null);
      setRefuseMotif('');
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisDepl.errGeneric'));
    } finally {
      setUpdatingRequestId(null);
    }
  };

  const handleSendSumupLink = async (requestId) => {
    const link = sumupLinkInput[requestId];
    if (!link) {
      alert(t('devisDepl.sumup.enterLinkAlert'));
      return;
    }
    setUpdatingRequestId(requestId);
    try {
      await api.put(`/admin/sumup-requests/${requestId}`, { sumup_link: link });
      setSumupRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'sent', sumup_link: link } : r));
      setSumupLinkInput(prev => ({ ...prev, [requestId]: '' }));
      alert(t('devisDepl.sumup.linkSentAlert'));
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisDepl.errGeneric'));
    } finally {
      setUpdatingRequestId(null);
    }
  };

  const handleCancelSumupRequest = async (requestId) => {
    setUpdatingRequestId(requestId);
    try {
      await api.put(`/admin/sumup-requests/${requestId}`, { status: 'cancelled' });
      setSumupRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'cancelled' } : r));
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisDepl.errGeneric'));
    } finally {
      setUpdatingRequestId(null);
    }
  };

  const fetchChartData = useCallback(async () => {
    try {
      const res = await api.get('/admin/devis/chart-data?days=30');
      setChartData(res.data || []);
    } catch (err) {
      console.error('Error fetching chart data:', err);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showChart) fetchChartData();
  }, [showChart, fetchChartData]);

  const resetForm = () => {
    setForm({
      client_nom: '', client_tel: '', client_email: '', client_adresse: '',
      description: '', inclure_deplacement: true, accept_online_payment: false,
      lignes: [{ designation: '', quantite: 1, prix: 0 }]
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!form.client_nom || !form.client_email) {
      alert(t('devisDepl.form.nameEmailRequired'));
      return;
    }
    if (form.lignes.length === 0 || !form.lignes.some(l => l.designation && l.prix > 0)) {
      alert(t('devisDepl.form.atLeastOneLine'));
      return;
    }
    try {
      if (editingId) {
        await api.put(`/admin/devis/${editingId}`, form);
      } else {
        await api.post('/admin/devis', form);
      }
      resetForm();
      fetchData();
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisDepl.errGeneric'));
    }
  };

  const handleEdit = (devis) => {
    setForm({
      client_nom: devis.client_nom,
      client_tel: devis.client_tel,
      client_email: devis.client_email,
      client_adresse: devis.client_adresse || '',
      description: devis.description || '',
      inclure_deplacement: devis.inclure_deplacement,
      accept_online_payment: devis.accept_online_payment || false,
      lignes: devis.lignes?.length ? devis.lignes : [{ designation: '', quantite: 1, prix: 0 }],
    });
    setEditingId(devis.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('devisDepl.confirmDelete'))) return;
    try {
      await api.delete(`/admin/devis/${id}`);
      fetchData();
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisDepl.errGeneric'));
    }
  };

  const handleSend = async (id) => {
    setSendingId(id);
    try {
      await api.post(`/admin/devis/${id}/send`);
      alert(t('devisDepl.sentByEmail'));
      fetchData();
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisDepl.errSend'));
    } finally {
      setSendingId(null);
    }
  };

  const handleSendReminders = async () => {
    if (!window.confirm(t('devisDepl.confirmSendReminders', { count: pendingReminders }))) return;
    setSendingReminders(true);
    try {
      const res = await api.post('/admin/devis/send-reminders', { days_threshold: 3 });
      alert(t('devisDepl.remindersSent', { count: res.data.reminders_sent }));
      fetchData();
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisDepl.errGeneric'));
    } finally {
      setSendingReminders(false);
    }
  };

  const handleExportCSV = () => {
    window.open(`${API}/api/admin/devis/export-csv`, '_blank');
  };

  const openPaymentModal = (devis) => {
    setPaymentModal({
      show: true,
      devisId: devis.id,
      devisRef: devis.ref || '',
      total: parseFloat(devis.total || 0),
    });
    setPaymentForm({ amount: String(parseFloat(devis.total || 0)), method: 'Espèces', note: '' });
  };

  const handleRegisterPayment = async () => {
    const amount = parseFloat(String(paymentForm.amount).replace(',', '.'));
    if (!amount || amount <= 0 || isNaN(amount)) {
      alert(t('devisDepl.invalidAmount'));
      return;
    }
    setProcessingPayment(true);
    try {
      const res = await api.post(`/admin/devis/${paymentModal.devisId}/manual-payment`, {
        amount,
        method: paymentForm.method,
        note: paymentForm.note,
      });
      alert(tError(res.data.message));
      setPaymentModal({ show: false, devisId: '', devisRef: '', total: 0 });
      fetchData();
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisDepl.errPayment'));
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleStaffAccept = async (devisId) => {
    if (!window.confirm(t('devisDepl.confirmStaffAccept'))) return;
    setProcessingAction(devisId);
    try {
      const res = await api.post(`/admin/devis/${devisId}/staff-accept`);
      alert(tError(res.data.message));
      fetchData();
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisDepl.errValidation'));
    } finally {
      setProcessingAction('');
    }
  };

  const handleSendReminder = async (devisId) => {
    setProcessingAction(devisId + '-reminder');
    try {
      const res = await api.post(`/admin/devis/${devisId}/send-reminder`);
      alert(tError(res.data.message) || t('devisDepl.reminderSent'));
      fetchData();
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisDepl.errSendReminder'));
    } finally {
      setProcessingAction('');
    }
  };

  const handleValidateBon = async (devisId) => {
    if (!window.confirm(t('devisDepl.confirmValidateBon'))) return;
    setProcessingAction(devisId);
    try {
      const res = await api.post(`/admin/devis/${devisId}/validate-bon`);
      alert(tError(res.data.message));
      fetchData();
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisDepl.errValidation'));
    } finally {
      setProcessingAction('');
    }
  };

  const handleSendFactureEmail = async (devisId) => {
    setProcessingAction(devisId);
    try {
      const res = await api.post(`/admin/devis/${devisId}/send-facture-email`);
      alert(tError(res.data.message));
      fetchData();
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisDepl.errSendFacture'));
    } finally {
      setProcessingAction('');
    }
  };

  const addLine = () => {
    setForm(f => ({ ...f, lignes: [...f.lignes, { designation: '', quantite: 1, prix: 0 }] }));
  };

  const removeLine = (idx) => {
    setForm(f => ({ ...f, lignes: f.lignes.filter((_, i) => i !== idx) }));
  };

  const updateLine = (idx, field, value) => {
    setForm(f => ({
      ...f,
      lignes: f.lignes.map((l, i) => i === idx ? { ...l, [field]: value } : l)
    }));
  };

  const calcTotal = () => {
    const sous = form.lignes.reduce((s, l) => s + (parseFloat(l.prix) || 0) * (parseInt(l.quantite) || 1), 0);
    return sous + (form.inclure_deplacement ? 45 : 0);
  };

  const filteredDevis = devisList.filter(d => {
    const matchSearch = !searchTerm ||
      d.ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.client_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.client_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = !filterStatus || d.status === filterStatus || d.payment_status === filterStatus;
    return matchSearch && matchStatus;
  });

  const fmtDate = (iso) => iso ? formatDate(iso, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-gray-900 border border-white/10 rounded-lg p-3 text-xs shadow-xl">
        <p className="text-gray-400 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="font-semibold">{p.name}: {p.value}</p>
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="devis-deplacement-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-black text-white flex items-center gap-3" data-testid="devis-page-title">
              <FileText className="w-7 h-7 text-violet-400" />
              {t('devisDepl.title')}
            </h1>
            <p className="text-sm text-gray-400 mt-1">{t('devisDepl.subtitle')}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {pendingReminders > 0 && (
              <button onClick={handleSendReminders} disabled={sendingReminders}
                className="px-3 py-2 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 font-semibold flex items-center gap-2 text-sm transition-colors disabled:opacity-50"
                data-testid="send-reminders-btn">
                <Bell className="w-4 h-4" />
                {sendingReminders ? t('devisDepl.sending') : t('devisDepl.remindBtn', { count: pendingReminders })}
              </button>
            )}
            <button onClick={() => setShowChart(!showChart)}
              className={`p-2 rounded-lg border transition-colors ${showChart ? 'bg-violet-500/20 text-violet-400 border-violet-500/30' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'}`}
              data-testid="toggle-chart-btn">
              <BarChart3 className="w-4 h-4" />
            </button>
            <button onClick={handleExportCSV}
              className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-gray-400 transition-colors"
              data-testid="export-csv-btn" title={t('devisDepl.exportCsv')}>
              <Download className="w-4 h-4" />
            </button>
            <button onClick={fetchData} className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-gray-400" data-testid="refresh-devis-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="px-4 py-2 rounded-lg text-white font-semibold flex items-center gap-2 transition-colors hover:brightness-110"
              style={{ background: 'var(--theme-primary, #dc2626)' }}
              data-testid="create-devis-btn"
            >
              <Plus className="w-4 h-4" /> {t('devisDepl.newDevis')}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="devis-stats">
          {[
            { testid: 'total', labelKey: 'devisDepl.stats.total', value: stats.total || 0, color: 'text-white' },
            { testid: 'en attente', labelKey: 'devisDepl.stats.pending', value: stats.en_attente || 0, color: 'text-yellow-400' },
            { testid: 'acceptés', labelKey: 'devisDepl.stats.accepted', value: stats.accepte || 0, color: 'text-green-400' },
            { testid: 'refusés', labelKey: 'devisDepl.stats.refused', value: stats.refuse || 0, color: 'text-red-400' },
            { testid: 'revenus', labelKey: 'devisDepl.stats.revenue', value: formatCurrency(stats.total_revenue || 0), color: 'text-emerald-400' },
          ].map((s, i) => (
            <div key={i} className="card-dark p-3 text-center" data-testid={`stat-${s.testid}`}>
              <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500">{t(s.labelKey)}</div>
            </div>
          ))}
        </div>

        {/* Incoming Quote Requests */}
        {devisRequests.length > 0 && (
          <div className="card-dark p-4" data-testid="devis-requests-panel">
            <button
              onClick={() => setShowRequests(!showRequests)}
              className="w-full flex items-center justify-between"
              data-testid="toggle-requests-panel"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/20">
                  <Bell className="w-4 h-4 text-cyan-400" />
                </div>
                <div className="text-left">
                  <span className="font-bold text-white text-sm">{t('devisDepl.requests.title')}</span>
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                    {t(devisRequests.filter(r => r.status === 'nouveau').length > 1 ? 'devisDepl.requests.newCountPlural' : 'devisDepl.requests.newCountSingular', { count: devisRequests.filter(r => r.status === 'nouveau').length })}
                  </span>
                </div>
              </div>
              {showRequests ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {showRequests && (
              <div className="mt-4 space-y-3">
                {devisRequests.map(req => (
                  <div key={req.id} className={`p-4 rounded-xl border transition-all ${
                    req.status === 'nouveau' ? 'bg-yellow-500/5 border-yellow-500/20' :
                    req.status === 'en_cours' ? 'bg-blue-500/5 border-blue-500/20' :
                    req.status === 'refuse' ? 'bg-red-500/5 border-red-500/20 opacity-60' :
                    'bg-green-500/5 border-green-500/20 opacity-60'
                  }`} data-testid={`staff-request-${req.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-white text-sm">{req.titre || t('devisDepl.requests.defaultTitle')}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                            req.status === 'nouveau' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                            req.status === 'en_cours' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                            req.status === 'refuse' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                            'bg-green-500/20 text-green-400 border-green-500/30'
                          }`}>
                            {req.status === 'nouveau' ? t('devisDepl.requests.statusNew') : req.status === 'en_cours' ? t('devisDepl.requests.statusInProgress') : req.status === 'refuse' ? t('devisDepl.requests.statusRefused') : t('devisDepl.requests.statusTreated')}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-gray-400 border border-white/10 uppercase font-bold">
                            {req.source === 'b2b' ? 'B2B' : t('devisDepl.requests.client')}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400">
                          <strong>{req.source_name}</strong>
                          {req.source_tel && <span className="ml-2">{req.source_tel}</span>}
                          {req.source_email && <span className="ml-2 text-cyan-400">{req.source_email}</span>}
                        </div>
                        {(req.marque || req.modele) && (
                          <div className="text-xs text-gray-500">{req.marque} {req.modele}</div>
                        )}
                        <p className="text-sm text-gray-300">{req.description}</p>
                        {req.adresse && <p className="text-xs text-gray-500">{t('devisDepl.requests.addressLabel')}: {req.adresse}</p>}
                        <div className="text-[10px] text-gray-600">{req.created_at ? formatDate(req.created_at, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</div>
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        {(req.status === 'nouveau' || req.status === 'en_cours') && (
                          <>
                            <button
                              onClick={() => prefillFromRequest(req)}
                              disabled={updatingRequestId === req.id}
                              className="px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold flex items-center gap-1 transition-colors"
                              data-testid={`create-devis-from-request-${req.id}`}
                            >
                              <Plus className="w-3 h-3" /> {t('devisDepl.requests.createDevis')}
                            </button>
                            <button
                              onClick={() => updateRequestStatus(req.id, 'traite')}
                              disabled={updatingRequestId === req.id}
                              className="px-3 py-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 text-xs font-semibold border border-green-500/30 transition-colors"
                              data-testid={`mark-treated-${req.id}`}
                            >
                              <CheckCircle className="w-3 h-3 inline mr-1" /> {t('devisDepl.requests.markTreated')}
                            </button>
                            <button
                              onClick={() => { setRefuseRequestId(refuseRequestId === req.id ? null : req.id); setRefuseMotif(''); }}
                              className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs font-semibold border border-red-500/30 transition-colors"
                              data-testid={`refuse-request-${req.id}`}
                            >
                              <XCircle className="w-3 h-3 inline mr-1" /> {t('devisDepl.requests.refuse')}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Refuse motif input */}
                    {refuseRequestId === req.id && (
                      <div className="mt-3 p-3 rounded-lg bg-red-500/5 border border-red-500/20 space-y-2">
                        <label className="block text-xs text-red-400 font-semibold">{t('devisDepl.requests.refuseMotifLabel')}</label>
                        <textarea
                          value={refuseMotif}
                          onChange={e => setRefuseMotif(e.target.value)}
                          className="input-dark w-full py-2 text-sm min-h-[60px]"
                          placeholder={t('devisDepl.requests.refuseMotifPlaceholder')}
                          data-testid={`refuse-motif-${req.id}`}
                        />
                        <div className="flex gap-2">
                          <button onClick={() => { setRefuseRequestId(null); setRefuseMotif(''); }}
                            className="px-3 py-1.5 rounded-lg bg-white/5 text-gray-400 text-xs border border-white/10">
                            {t('devisDepl.cancel')}
                          </button>
                          <button onClick={() => handleRefuse(req.id)} disabled={updatingRequestId === req.id}
                            className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold disabled:opacity-50"
                            data-testid={`confirm-refuse-${req.id}`}>
                            {t('devisDepl.requests.confirmRefuse')}
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Show motif if refused */}
                    {req.status === 'refuse' && req.motif_refus && (
                      <div className="mt-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-300">
                        <strong>{t('devisDepl.requests.refuseMotifShownLabel')}</strong> {req.motif_refus}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SumUp Payment Link Requests */}
        {sumupRequests.filter(r => r.status === 'pending').length > 0 && (
          <div className="card-dark p-4" data-testid="sumup-requests-panel">
            <button
              onClick={() => setShowSumupRequests(!showSumupRequests)}
              className="w-full flex items-center justify-between"
              data-testid="toggle-sumup-requests"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <CreditCard className="w-4 h-4 text-blue-400" />
                </div>
                <div className="text-left">
                  <span className="font-bold text-white text-sm">{t('devisDepl.sumup.title')}</span>
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    {t('devisDepl.sumup.pendingCount', { count: sumupRequests.filter(r => r.status === 'pending').length })}
                  </span>
                </div>
              </div>
              {showSumupRequests ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {showSumupRequests && (
              <div className="mt-4 space-y-3">
                {sumupRequests.filter(r => r.status === 'pending').map(req => (
                  <div key={req.id} className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/20" data-testid={`sumup-request-${req.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-white text-sm">{req.doc_ref}</span>
                          <span className="text-xs text-blue-400 font-bold">{formatCurrency(req.total || 0)}</span>
                        </div>
                        <div className="text-xs text-gray-400">
                          <strong>{req.client_nom}</strong>
                          {req.client_email && <span className="ml-2 text-blue-400">{req.client_email}</span>}
                          {req.client_tel && <span className="ml-2">{req.client_tel}</span>}
                        </div>
                        <div className="text-[10px] text-gray-500">{fmtDate(req.created_at)}</div>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2 items-center">
                      <input
                        type="url"
                        placeholder={t('devisDepl.sumup.linkPlaceholder')}
                        value={sumupLinkInput[req.id] || ''}
                        onChange={e => setSumupLinkInput(prev => ({ ...prev, [req.id]: e.target.value }))}
                        className="input-dark flex-1 text-sm py-1.5"
                        data-testid={`sumup-link-input-${req.id}`}
                      />
                      <button
                        onClick={() => handleSendSumupLink(req.id)}
                        disabled={updatingRequestId === req.id || !sumupLinkInput[req.id]}
                        className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50 flex items-center gap-1"
                        data-testid={`sumup-send-link-${req.id}`}
                      >
                        <Send className="w-3 h-3" /> {t('devisDepl.send')}
                      </button>
                      <button
                        onClick={() => handleCancelSumupRequest(req.id)}
                        disabled={updatingRequestId === req.id}
                        className="px-2 py-1.5 rounded-lg bg-red-500/20 text-red-400 text-xs font-semibold hover:bg-red-500/30 disabled:opacity-50"
                        data-testid={`sumup-cancel-${req.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Chart */}
        {showChart && (
          <div className="card-dark p-5" data-testid="devis-chart">
            <h3 className="text-sm font-bold text-gray-400 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-violet-400" /> {t('devisDepl.chart.title')}
            </h3>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="accepte" name={t('devisDepl.chart.accepted')} fill="#22c55e" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="en_attente" name={t('devisDepl.chart.pending')} fill="#eab308" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="refuse" name={t('devisDepl.chart.refused')} fill="#ef4444" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-500 py-8">{t('devisDepl.chart.noData')}</p>
            )}
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="card-dark p-5 border border-violet-500/30 space-y-4" data-testid="devis-form">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-violet-400">{editingId ? t('devisDepl.form.editTitle') : t('devisDepl.form.newTitle')}</h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-white" data-testid="close-form-btn"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="relative">
                <label className="block text-xs text-gray-400 mb-1">{t('devisDepl.form.clientName')}</label>
                <input type="text" value={form.client_nom}
                  onChange={e => { setForm({ ...form, client_nom: e.target.value }); }}
                  onFocus={() => { if (clientSuggestions.length) setShowSuggestions(true); }}
                  className="input-dark w-full" placeholder={t('devisDepl.form.searchPlaceholder')} data-testid="form-client-nom"
                  autoComplete="off" />
                {searchingClients && <div className="absolute right-2 top-7 text-xs text-gray-500">...</div>}
                {showSuggestions && clientSuggestions.length > 0 && (
                  <div ref={suggestionsRef}
                    className="absolute z-50 top-full left-0 right-0 mt-1 bg-gray-900 border border-white/10 rounded-lg shadow-2xl max-h-48 overflow-y-auto"
                    data-testid="client-suggestions">
                    {clientSuggestions.map((c, i) => (
                      <button key={i} type="button"
                        onClick={() => selectClient(c)}
                        className="w-full text-left px-3 py-2 hover:bg-violet-500/20 transition-colors border-b border-white/5 last:border-0"
                        data-testid={`suggestion-${i}`}>
                        <div className="flex items-center gap-2">
                          {c.type === 'b2b' ? <Building2 className="w-3 h-3 text-blue-400 shrink-0" /> : <span className="w-3 h-3 rounded-full bg-green-500/40 shrink-0 inline-block"></span>}
                          <span className="text-sm text-white font-semibold truncate">{c.nom}</span>
                          <span className="text-xs text-gray-500 ml-auto">{c.type === 'b2b' ? 'B2B' : t('devisDepl.requests.client')}</span>
                        </div>
                        <div className="text-xs text-gray-400 ml-5 truncate">
                          {c.email && <span>{c.email}</span>}
                          {c.tel && <span className="ml-2">{c.tel}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('devisDepl.form.phone')}</label>
                <input type="text" value={form.client_tel}
                  onChange={e => { setForm({ ...form, client_tel: e.target.value }); if (e.target.value.length >= 3) searchClients(e.target.value); }}
                  className="input-dark w-full" placeholder="0694..." data-testid="form-client-tel" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('devisDepl.form.email')}</label>
                <input type="email" value={form.client_email}
                  onChange={e => { setForm({ ...form, client_email: e.target.value }); if (e.target.value.length >= 3) searchClients(e.target.value); }}
                  className="input-dark w-full" placeholder={t('devisDepl.form.emailPlaceholder')} data-testid="form-client-email" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('devisDepl.form.address')}</label>
                <input type="text" value={form.client_adresse} onChange={e => setForm({ ...form, client_adresse: e.target.value })}
                  className="input-dark w-full" placeholder={t('devisDepl.form.addressPlaceholder')} data-testid="form-client-adresse" />
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('devisDepl.form.descriptionLabel')}</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                className="input-dark w-full resize-none" rows={2} placeholder={t('devisDepl.form.descriptionPlaceholder')} data-testid="form-description" />
            </div>

            {/* Lines */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-400 font-semibold">{t('devisDepl.form.lines')}</label>
                <button onClick={addLine} className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1" data-testid="add-line-btn">
                  <Plus className="w-3 h-3" /> {t('devisDepl.form.addLine')}
                </button>
              </div>
              <div className="space-y-2">
                {form.lignes.map((line, idx) => (
                  <div key={idx} className="flex gap-2 items-center" data-testid={`line-${idx}`}>
                    <input type="text" value={line.designation} onChange={e => updateLine(idx, 'designation', e.target.value)}
                      className="input-dark flex-1" placeholder={t('devisDepl.form.designation')} data-testid={`line-designation-${idx}`} />
                    <input type="number" value={line.quantite} onChange={e => updateLine(idx, 'quantite', parseInt(e.target.value) || 1)}
                      className="input-dark w-16 text-center" min="1" data-testid={`line-qty-${idx}`} />
                    <input type="number" value={line.prix} onChange={e => updateLine(idx, 'prix', parseFloat(e.target.value) || 0)}
                      className="input-dark w-24 text-right" step="0.01" placeholder={t('devisDepl.form.price')} data-testid={`line-prix-${idx}`} />
                    <span className="text-xs text-gray-500 w-8">€</span>
                    {form.lignes.length > 1 && (
                      <button onClick={() => removeLine(idx)} className="text-red-400 hover:text-red-300" data-testid={`remove-line-${idx}`}>
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Travel fee */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={form.inclure_deplacement}
                  onChange={e => setForm({ ...form, inclure_deplacement: e.target.checked })}
                  className="w-4 h-4 rounded accent-violet-500" data-testid="form-deplacement-checkbox" />
                <span className="text-sm text-gray-300">{t('devisDepl.form.travelFee', { amount: formatCurrency(45) })}</span>
              </div>
              <span className="font-bold text-violet-400">{form.inclure_deplacement ? formatCurrency(45) : formatCurrency(0)}</span>
            </div>

            {/* Accept online payment toggle - hidden when globally disabled */}
            {!disableOnlinePayment && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={form.accept_online_payment}
                    onChange={e => setForm({ ...form, accept_online_payment: e.target.checked })}
                    className="w-4 h-4 rounded accent-blue-500" data-testid="form-accept-online-payment" />
                  <div>
                    <span className="text-sm text-gray-300">{t('devisDepl.form.acceptOnline')}</span>
                    <p className="text-[11px] text-gray-500">{t('devisDepl.form.acceptOnlineHint')}</p>
                  </div>
                </div>
                <CreditCard className="w-4 h-4 text-blue-400" />
              </div>
            )}

            {/* Total */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-violet-500/10 border border-violet-500/30">
              <span className="font-bold text-white">{t('devisDepl.form.total')}</span>
              <span className="text-xl font-black text-violet-400" data-testid="form-total">{formatCurrency(calcTotal())}</span>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={resetForm} className="px-4 py-2 text-sm text-gray-400 hover:text-white">{t('devisDepl.cancel')}</button>
              <button onClick={handleSubmit}
                className="px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-semibold transition-colors"
                data-testid="submit-devis-btn">
                {editingId ? t('devisDepl.form.submitEdit') : t('devisDepl.form.submitCreate')}
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-3 flex-wrap" data-testid="devis-filters">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="input-dark w-full pl-10" placeholder={t('devisDepl.searchPlaceholder')}
              data-testid="devis-search" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="input-dark" data-testid="devis-filter-status">
            <option value="">{t('devisDepl.filter.all')}</option>
            <option value="en_attente">{t('devisDepl.status.pending')}</option>
            <option value="accepte">{t('devisDepl.status.accepted')}</option>
            <option value="refuse">{t('devisDepl.status.refused')}</option>
            <option value="acompte_verse">{t('devisDepl.status.depositPaid')}</option>
            <option value="paye">{t('devisDepl.status.paid')}</option>
          </select>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="loading-spinner"></div></div>
        ) : filteredDevis.length === 0 ? (
          <div className="text-center py-12 text-gray-400" data-testid="devis-empty">
            <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>{searchTerm || filterStatus ? t('devisDepl.noResults') : t('devisDepl.empty')}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="devis-list">
            {filteredDevis.map(devis => {
              const st = statusConfig[devis.payment_status] || statusConfig[devis.status] || statusConfig.en_attente;
              const StIcon = st.icon;
              const isExpanded = expandedId === devis.id;

              return (
                <div key={devis.id} className="card-dark overflow-hidden" data-testid={`devis-card-${devis.ref}`}>
                  <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : devis.id)}>
                    <div className="flex items-center gap-4">
                      <div className="flex flex-col">
                        <span className="font-bold text-violet-400" data-testid={`devis-ref-${devis.ref}`}>{devis.ref}</span>
                        <span className="text-xs text-gray-500">{fmtDate(devis.created_at)}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-white">{devis.client_nom}</span>
                          {devis.is_b2b && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center gap-1">
                              <Building2 className="w-3 h-3" /> B2B
                            </span>
                          )}
                          {devis.accept_online_payment && !disableOnlinePayment && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center gap-1">
                              <CreditCard className="w-3 h-3" /> SumUp
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">{devis.client_email}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-lg text-white">{formatCurrency(devis.total || 0)}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-bold border ${st.color} flex items-center gap-1`}>
                        <StIcon className="w-3 h-3" /> {t(st.labelKey)}
                      </span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-white/10 p-4 space-y-3 bg-white/[0.02]">
                      {/* Client info */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><span className="text-gray-500">{t('devisDepl.detail.phone')}</span> <span className="text-gray-300">{devis.client_tel || '-'}</span></div>
                        <div><span className="text-gray-500">{t('devisDepl.detail.address')}</span> <span className="text-gray-300">{devis.client_adresse || '-'}</span></div>
                        <div><span className="text-gray-500">{t('devisDepl.detail.purchaseOrder')}</span> <span className="text-green-400 font-semibold">{devis.bon_commande_ref || '-'}</span></div>
                        <div><span className="text-gray-500">{t('devisDepl.detail.invoice')}</span> <span className="text-red-400 font-semibold">{devis.facture_ref || '-'}</span></div>
                      </div>

                      {devis.description && (
                        <p className="text-sm text-gray-400 italic">{devis.description}</p>
                      )}

                      {/* Lines */}
                      <div className="rounded-lg border border-white/10 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-white/5">
                              <th className="text-left p-2 text-gray-400 font-medium">{t('devisDepl.table.designation')}</th>
                              <th className="text-center p-2 text-gray-400 font-medium w-16">{t('devisDepl.table.qty')}</th>
                              <th className="text-right p-2 text-gray-400 font-medium w-24">{t('devisDepl.table.price')}</th>
                              <th className="text-right p-2 text-gray-400 font-medium w-24">{t('devisDepl.table.total')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {devis.lignes?.map((l, i) => (
                              <tr key={i} className="border-t border-white/5">
                                <td className="p-2 text-gray-300">{l.designation}</td>
                                <td className="p-2 text-center text-gray-300">{l.quantite}</td>
                                <td className="p-2 text-right text-gray-300">{formatCurrency(parseFloat(l.prix))}</td>
                                <td className="p-2 text-right text-white font-medium">{formatCurrency(parseFloat(l.prix) * parseInt(l.quantite))}</td>
                              </tr>
                            ))}
                            {devis.inclure_deplacement && (
                              <tr className="border-t border-white/5">
                                <td className="p-2 text-gray-300">{t('devisDepl.travelFeeLabel')}</td>
                                <td className="p-2 text-center text-gray-300">1</td>
                                <td className="p-2 text-right text-gray-300">{formatCurrency(45)}</td>
                                <td className="p-2 text-right text-white font-medium">{formatCurrency(45)}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Email sent info */}
                      {devis.email_sent && (
                        <p className="text-xs text-green-400">{t('devisDepl.detail.emailSentOn', { date: fmtDate(devis.email_sent_at) })}</p>
                      )}
                      {devis.reminder_sent && (
                        <p className="text-xs text-amber-400">{t('devisDepl.detail.reminderSentOn', { date: fmtDate(devis.reminder_sent_at) })}</p>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2 flex-wrap pt-2">
                        {devis.status === 'en_attente' && (
                          <>
                            <button onClick={() => handleStaffAccept(devis.id)} disabled={processingAction === devis.id}
                              className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                              data-testid={`accept-devis-${devis.ref}`}>
                              <CheckCircle className="w-3.5 h-3.5" /> {t('devisDepl.actions.validateDevis')}
                            </button>
                            <button onClick={() => handleSend(devis.id)} disabled={sendingId === devis.id}
                              className="px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
                              data-testid={`send-devis-${devis.ref}`}>
                              <Send className="w-3.5 h-3.5" /> {sendingId === devis.id ? t('devisDepl.sending') : t('devisDepl.actions.sendByEmail')}
                            </button>
                            {devis.email_sent && (
                              <button onClick={() => handleSendReminder(devis.id)} disabled={processingAction === devis.id + '-reminder'}
                                className="px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
                                data-testid={`remind-devis-${devis.ref}`}>
                                <Bell className="w-3.5 h-3.5" /> {processingAction === devis.id + '-reminder' ? t('devisDepl.sending') : t('devisDepl.actions.remindAcceptance')}
                              </button>
                            )}
                            <button onClick={() => handleEdit(devis)}
                              className="px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 text-xs font-semibold flex items-center gap-1.5"
                              data-testid={`edit-devis-${devis.ref}`}>
                              <Edit3 className="w-3.5 h-3.5" /> {t('devisDepl.actions.edit')}
                            </button>
                          </>
                        )}
                        {devis.status === 'accepte' && devis.bon_commande_ref && !devis.bon_validated && (
                          <button onClick={() => handleValidateBon(devis.id)} disabled={processingAction === devis.id}
                            className="px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                            data-testid={`validate-bon-${devis.ref}`}>
                            <CheckCircle className="w-3.5 h-3.5" /> {t('devisDepl.actions.validateBon')}
                          </button>
                        )}
                        {devis.status === 'accepte' && devis.bon_validated && (
                          <span className="px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-300 border border-amber-500/20 text-xs font-semibold flex items-center gap-1.5">
                            <CheckCircle className="w-3.5 h-3.5" /> {t('devisDepl.actions.bonValidated')}
                          </span>
                        )}
                        {devis.status === 'accepte' && devis.payment_status !== 'paye' && (
                          <button onClick={() => handleSendReminder(devis.id)} disabled={processingAction === devis.id + '-reminder'}
                            className="px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
                            data-testid={`remind-payment-${devis.ref}`}>
                            <Bell className="w-3.5 h-3.5" /> {processingAction === devis.id + '-reminder' ? t('devisDepl.sending') : t('devisDepl.actions.remindPayment')}
                          </button>
                        )}
                        <a href={`${API}/api/devis/${devis.id}/pdf`} target="_blank" rel="noreferrer"
                          className="px-3 py-1.5 rounded-lg bg-violet-500/20 text-violet-400 border border-violet-500/30 hover:bg-violet-500/30 text-xs font-semibold flex items-center gap-1.5"
                          data-testid={`pdf-devis-${devis.ref}`}>
                          <Download className="w-3.5 h-3.5" /> {t('devisDepl.actions.pdfDevis')}
                        </a>
                        {devis.bon_commande_ref && (
                          <a href={`${API}/api/devis/${devis.id}/bon-pdf`} target="_blank" rel="noreferrer"
                            className="px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 text-xs font-semibold flex items-center gap-1.5"
                            data-testid={`pdf-bon-${devis.ref}`}>
                            <Download className="w-3.5 h-3.5" /> {t('devisDepl.actions.pdfBon')}
                          </a>
                        )}
                        {devis.facture_ref && (
                          <>
                            <a href={`${API}/api/facture-dep/${devis.id}/pdf`} target="_blank" rel="noreferrer"
                              className="px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 text-xs font-semibold flex items-center gap-1.5">
                              <Download className="w-3.5 h-3.5" /> {t('devisDepl.actions.pdfFacture')}
                            </a>
                            <button onClick={() => handleSendFactureEmail(devis.id)} disabled={processingAction === devis.id}
                              className="px-3 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                              data-testid={`send-facture-${devis.ref}`}>
                              <Send className="w-3.5 h-3.5" /> {t('devisDepl.actions.sendFactureEmail')}
                            </button>
                          </>
                        )}
                        {devis.payment_status !== 'paye' && (
                          <button onClick={() => openPaymentModal(devis)}
                            className="px-3 py-1.5 rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30 text-xs font-semibold flex items-center gap-1.5"
                            data-testid={`pay-devis-${devis.ref}`}>
                            <Banknote className="w-3.5 h-3.5" /> {t('devisDepl.actions.registerPayment')}
                          </button>
                        )}
                        {devis.payment_status === 'paye' && (
                          <span className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-semibold flex items-center gap-1.5">
                            <CheckCircle className="w-3.5 h-3.5" /> {t('devisDepl.status.paid')}
                          </span>
                        )}
                        {isAdmin && (
                        <button onClick={() => handleDelete(devis.id)}
                          className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 text-xs font-semibold flex items-center gap-1.5 ml-auto"
                          data-testid={`delete-devis-${devis.ref}`}>
                          <Trash2 className="w-3.5 h-3.5" /> {t('devisDepl.actions.delete')}
                        </button>
                        )}
                        {/* Attach / Detach devis to repair */}
                        {devis.linked_repair_ref ? (
                          <button onClick={async () => {
                            if (!window.confirm(t('devisDepl.confirmDetach', { ref: devis.linked_repair_ref }))) return;
                            try {
                              await api.post(`/devices/${devis.linked_repair_ref}/detach-devis`);
                              fetchData();
                            } catch (err) { alert(tError(err.response?.data?.detail) || t('devisDepl.errGeneric')); }
                          }}
                            className="px-3 py-1.5 rounded-lg bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/20 text-xs font-semibold flex items-center gap-1.5"
                            data-testid={`detach-devis-${devis.ref}`}>
                            <Unlink className="w-3.5 h-3.5" /> {t('devisDepl.actions.linkedTo', { ref: devis.linked_repair_ref })}
                          </button>
                        ) : (
                          <button onClick={() => {
                            const lines = devis.lines || [];
                            const total = lines.reduce((s, l) => s + (l.quantity || 1) * (l.unit_price || 0), 0);
                            setAttachModal({ show: true, devisId: devis.id, devisRef: devis.ref, devisTotal: total });
                          }}
                            className="px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 text-xs font-semibold flex items-center gap-1.5"
                            data-testid={`attach-devis-${devis.ref}`}>
                            <Link2 className="w-3.5 h-3.5" /> {t('devisDepl.actions.attachToRepair')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {paymentModal.show && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", zIndex: 9999}}>
          <div className="modal-dark w-full max-w-md">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2"><Banknote className="w-5 h-5 text-cyan-400" /> {t('devisDepl.payment.title')}</h3>
              <button onClick={() => setPaymentModal({ show: false, devisId: '', devisRef: '', total: 0 })} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-400">{t('devisDepl.payment.devisLabel')} <span className="text-cyan-400 font-mono">{paymentModal.devisRef}</span></p>
              <p className="text-sm text-gray-400">{t('devisDepl.payment.totalLabel')} <span className="text-green-400 font-bold">{formatCurrency(paymentModal.total)}</span></p>

              <div>
                <label className="block text-sm text-gray-300 mb-2">{t('devisDepl.payment.methodLabel')}</label>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map(m => (
                    <button
                      key={m}
                      onClick={() => setPaymentForm(f => ({ ...f, method: m }))}
                      className={`py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                        paymentForm.method === m
                          ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50'
                          : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10'
                      }`}
                      data-testid={`payment-method-${m}`}
                    >
                      {t(PAYMENT_METHOD_LABEL_KEYS[m])}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">{t('devisDepl.payment.amount')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                  className="input-dark w-full py-3 text-lg font-bold"
                  data-testid="payment-amount"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">{t('devisDepl.payment.noteLabel')}</label>
                <input
                  type="text"
                  value={paymentForm.note}
                  onChange={(e) => setPaymentForm(f => ({ ...f, note: e.target.value }))}
                  placeholder={t('devisDepl.payment.notePlaceholder')}
                  className="input-dark w-full py-2"
                  data-testid="payment-note"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setPaymentModal({ show: false, devisId: '', devisRef: '', total: 0 })}
                  className="flex-1 py-3 rounded-xl bg-gray-700 hover:bg-gray-600 font-bold"
                >
                  {t('devisDepl.cancel')}
                </button>
                <button
                  onClick={handleRegisterPayment}
                  disabled={processingPayment || !paymentForm.amount}
                  className="flex-1 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-700 font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="payment-submit"
                >
                  {processingPayment ? (
                    <div className="loading-spinner w-5 h-5"></div>
                  ) : (
                    <><CreditCard className="w-4 h-4" /> {t('devisDepl.payment.submit')}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AttachDevisModal
        isOpen={attachModal.show}
        onClose={() => setAttachModal({ show: false, devisId: '', devisRef: '', devisTotal: 0 })}
        devisId={attachModal.devisId}
        devisType="deplacement"
        devisRef={attachModal.devisRef}
        devisTotal={attachModal.devisTotal}
        onAttached={fetchData}
      />
    </Layout>
  );
};

export default DevisDeplacementPage;
